import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Tabs,
  Tab,
} from '@mui/material';
import axios from 'axios';
import { CardGiftcard as RewardIcon } from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// Derive server base (strip any trailing /api) so we can resolve static files
const SERVER_BASE = API_BASE.includes('/api') ? API_BASE.substring(0, API_BASE.indexOf('/api')) : API_BASE;

export default function Rewards() {
  const [currentTab, setCurrentTab] = useState('catalog');
  const [rewardDialog, setRewardDialog] = useState(false);
  const [selectedReward, setSelectedReward] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [balance, setBalance] = useState({ money: 0, points: 0 });
  const [rewards, setRewards] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [address, setAddress] = useState({
    fullName: '',
    phone: '',
    country: '',
    city: '',
    street: '',
    postalCode: ''
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const [balRes, rwRes, claimsRes] = await Promise.all([
          axios.get(`${API_BASE}/rewards/balance`, { headers }),
          axios.get(`${API_BASE}/rewards/catalog`, { headers }),
          axios.get(`${API_BASE}/rewards/my-claims`, { headers })
        ]);
        console.log('Balance response:', balRes.data);
        console.log('Rewards response:', rwRes.data);
        setBalance(balRes.data || { money: 0, points: 0 });
        setRewards(rwRes.data || []);
        setClaims(Array.isArray(claimsRes.data) ? claimsRes.data : []);
        setError(null);
      } catch (e) {
        console.error('Не удалось загрузить награды', e);
        setError('Не удалось загрузить награды');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleRewardClaim = async () => {
    if (!selectedReward) return;
    
    // Установить флаг попытки отправки для отображения ошибок
    setAttemptedSubmit(true);
    
    // Проверка обязательных полей
    const requiredFields = ['fullName', 'phone', 'country', 'city', 'street', 'postalCode'];
    const missingFields = requiredFields.filter(field => !address[field] || address[field].trim() === '');
    
    if (missingFields.length > 0) {
      setError('Пожалуйста, заполните все обязательные поля адреса доставки');
      return;
    }
    
    try {
      setProcessing(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // Обязательные детали для доставки
      await axios.post(`${API_BASE}/rewards/claim`, {
        rewardId: selectedReward.id,
        deliveryDetails: address
      }, { headers });
      setRewardDialog(false);
      setAttemptedSubmit(false);
      // Refresh data
      const balRes = await axios.get(`${API_BASE}/rewards/balance`, { headers });
      setBalance(balRes.data || { money: 0, points: 0 });
      setError(null);
    } catch (e) {
      console.error('Reward claim failed', e);
      setError(e.response?.data?.error || 'Не удалось заказать награду');
    } finally {
      setProcessing(false);
    }
  };

  const openRewardDialog = (reward) => {
    setSelectedReward(reward);
    setAttemptedSubmit(false);
    setError(null);
    setRewardDialog(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 1.5, md: 4 },
          mx: { xs: 0, md: 3 },
          mb: 3,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '24px', md: '32px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography 
          variant="h5" 
          gutterBottom
          sx={{ 
            fontWeight: 700,
            color: '#2d3748',
            mb: 2
          }}
        >
          Подарки за баллы
        </Typography>

        {/* Balance Card */}
        <Box 
          sx={{ 
            p: 3,
            mb: 3,
            background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
            borderRadius: { xs: '16px', md: '20px' },
            boxShadow: '0 4px 20px rgba(253, 203, 110, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.7)', fontWeight: 600, mb: 1 }}>
              Ваш баланс баллов
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2d3748' }}>
              {Number(balance.points || 0).toFixed(1)}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RewardIcon sx={{ fontSize: 32, color: '#2d3748' }} />
          </Box>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              borderRadius: { xs: '12px', md: '16px' },
              '& .MuiAlert-icon': { color: '#d63031' }
            }}
          >
            {error}
          </Alert>
        )}

        <Tabs
          value={currentTab}
          onChange={(e, v) => setCurrentTab(v)}
          sx={{ 
            mb: 3,
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '1rem',
              textTransform: 'none',
              color: '#718096',
              '&.Mui-selected': {
                color: '#0984e3',
                fontWeight: 700
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#0984e3',
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab value="catalog" label="Каталог" />
          <Tab value="my-claims" label="Мои заявки" />
        </Tabs>

        {currentTab === 'catalog' && (
          <Grid container spacing={3}>
            {rewards.length === 0 ? (
              <Grid item xs={12}>
                <Typography color="text.secondary" align="center">
                  Нет доступных наград
                </Typography>
              </Grid>
            ) : (
              rewards
                .filter(reward => reward.stock === undefined || reward.stock > 0) // Hide out of stock items
                .map((reward) => {
                const imageSrc = reward.image?.startsWith('/uploads')
                  ? `${SERVER_BASE}${reward.image}`
                  : reward.image || 'https://via.placeholder.com/300x200?text=No+Image';
                const rewardPoints = Number(reward.points) || 0;
                const userPoints = Number(balance.points) || 0;
                const canClaim = userPoints >= rewardPoints;
                const outOfStock = reward.stock !== undefined && reward.stock <= 0;
                const isDisabled = !canClaim || outOfStock;
                return (
                  <Grid item xs={12} sm={6} md={4} key={reward.id}>
                    <Card 
                      elevation={0}
                      sx={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: { xs: '16px', md: '20px' },
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
                        }
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="200"
                        image={imageSrc}
                        alt={reward.title}
                        sx={{ borderRadius: '20px 20px 0 0' }}
                      />
                      <CardContent>
                        <Typography 
                          variant="h6" 
                          gutterBottom
                          sx={{ fontWeight: 700, color: '#2d3748' }}
                        >
                          {reward.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          gutterBottom
                          sx={{ color: '#718096', mb: 2 }}
                        >
                          {reward.description}
                        </Typography>
                        <Box 
                          sx={{ 
                            display: 'inline-block',
                            px: 2,
                            py: 0.5,
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
                            mb: 1
                          }}
                        >
                          <Typography 
                            variant="h6" 
                            sx={{ fontWeight: 700, color: '#2d3748' }}
                          >
                            {rewardPoints.toFixed(1)} баллов
                          </Typography>
                        </Box>
                      </CardContent>
                      <CardActions sx={{ px: 2, pb: 2 }}>
                        <Button
                          variant="contained"
                          startIcon={<RewardIcon />}
                          disabled={isDisabled}
                          onClick={() => openRewardDialog(reward)}
                          fullWidth
                          sx={{
                            background: isDisabled 
                              ? 'rgba(0, 0, 0, 0.12)' 
                              : 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                            color: isDisabled ? 'rgba(0, 0, 0, 0.26)' : 'white',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '12px',
                            py: 1,
                            boxShadow: isDisabled ? 'none' : '0 4px 15px rgba(9, 132, 227, 0.3)',
                            '&:hover': {
                              background: isDisabled 
                                ? 'rgba(0, 0, 0, 0.12)'
                                : 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)'
                            }
                          }}
                        >
                          {outOfStock ? 'Нет в наличии' : (!canClaim ? 'Недостаточно баллов' : 'Заказать награду')}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>
        )}

        {currentTab === 'my-claims' && (
          <Box>
            {claims.length === 0 ? (
              <Typography 
                color="text.secondary"
                sx={{ 
                  textAlign: 'center',
                  py: 4,
                  color: '#718096',
                  fontWeight: 600 
                }}
              >
                Заявок пока нет
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {claims.map((claim) => {
                  const reward = rewards.find(r => r.id === claim.rewardId);
                  const title = reward?.title || `Подарок #${claim.rewardId}`;
                  const statusLabel = ({
                    pending: 'В обработке',
                    shipped: 'Отправлен',
                    delivered: 'Доставлен',
                    cancelled: 'Отменён'
                  }[claim.status]) || claim.status;
                  const statusColor = (
                    claim.status === 'delivered' ? '#56ab2f' :
                    claim.status === 'shipped' ? '#0984e3' :
                    claim.status === 'cancelled' ? '#d63031' : '#fdcb6e'
                  );
                  const statusBg = (
                    claim.status === 'delivered' ? 'linear-gradient(135deg, rgba(86, 171, 47, 0.15), rgba(45, 122, 31, 0.15))' :
                    claim.status === 'shipped' ? 'linear-gradient(135deg, rgba(116, 185, 255, 0.15), rgba(9, 132, 227, 0.15))' :
                    claim.status === 'cancelled' ? 'linear-gradient(135deg, rgba(255, 118, 117, 0.15), rgba(214, 48, 49, 0.15))' :
                    'linear-gradient(135deg, rgba(255, 234, 167, 0.15), rgba(253, 203, 110, 0.15))'
                  );
                  return (
                    <Grid item xs={12} md={6} key={claim.id}>
                      <Box 
                        sx={{ 
                          p: 2.5,
                          background: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: { xs: '16px', md: '20px' },
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 6px 25px rgba(0, 0, 0, 0.12)'
                          }
                        }}
                      >
                        <Typography 
                          variant="subtitle1"
                          sx={{ 
                            fontWeight: 700,
                            color: '#2d3748',
                            mb: 1.5 
                          }}
                        >
                          {title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#718096',
                            mb: 2,
                            fontWeight: 500 
                          }}
                        >
                          Заявка от {new Date(claim.createdAt).toLocaleString('ru-RU')}
                        </Typography>
                        <Box
                          sx={{
                            display: 'inline-block',
                            px: 2,
                            py: 0.75,
                            borderRadius: '12px',
                            background: statusBg,
                            mb: 1.5
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: statusColor,
                              fontWeight: 700
                            }}
                          >
                            Статус: {statusLabel}
                          </Typography>
                        </Box>
                        {claim.trackingNumber && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mt: 1,
                              color: '#2d3748',
                              fontWeight: 600 
                            }}
                          >
                            Трек-номер: <strong>{claim.trackingNumber}</strong>
                          </Typography>
                        )}
                        {claim.shippingComment && (
                          <Typography 
                            variant="caption" 
                            display="block" 
                            sx={{ 
                              mt: 1,
                              color: '#718096',
                              fontWeight: 500 
                            }}
                          >
                            Комментарий: {claim.shippingComment}
                          </Typography>
                        )}
                        {claim.status === 'cancelled' && claim.rejectionComment && (
                          <Alert 
                            severity="error" 
                            sx={{ 
                              mt: 1.5,
                              borderRadius: '12px',
                              '& .MuiAlert-icon': { color: '#d63031' }
                            }}
                          >
                            Причина: {claim.rejectionComment}
                          </Alert>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}
      </Paper>

      

      {/* Reward Claim Dialog */}
      <Dialog
        open={rewardDialog}
        onClose={() => setRewardDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: { xs: '8px', md: '32px' },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        {selectedReward && (
          <>
            <DialogTitle 
              sx={{ 
                fontWeight: 700,
                color: '#2d3748',
                fontSize: '1.25rem'
              }}
            >
              Оформление награды: {selectedReward.title}
            </DialogTitle>
            <DialogContent>
              <Box 
                sx={{ 
                  mb: 3,
                  p: 2,
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, rgba(255, 234, 167, 0.2), rgba(253, 203, 110, 0.2))',
                  border: '1px solid rgba(253, 203, 110, 0.3)'
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#2d3748',
                    fontWeight: 600,
                    mb: 0.5 
                  }}
                >
                  Стоимость: {(Number(selectedReward.points) || 0).toFixed(1)} баллов
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#2d3748',
                    fontWeight: 600 
                  }}
                >
                  Ваш баланс: {(Number(balance.points) || 0).toFixed(1)} баллов
                </Typography>
              </Box>
              {attemptedSubmit && error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 2,
                    borderRadius: '12px',
                    '& .MuiAlert-icon': { color: '#d63031' }
                  }}
                >
                  {error}
                </Alert>
              )}
              <Typography 
                variant="subtitle2" 
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  color: '#2d3748',
                  mb: 2 
                }}
              >
                Адрес доставки:
              </Typography>
              <TextField
                fullWidth
                label="ФИО"
                value={address.fullName}
                onChange={(e) => setAddress({ ...address, fullName: e.target.value })}
                error={attemptedSubmit && !address.fullName}
                helperText={attemptedSubmit && !address.fullName ? 'Обязательное поле' : ''}
                required
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: 2
                    }
                  }
                }}
              />
              <TextField
                fullWidth
                label="Телефон"
                value={address.phone}
                onChange={(e) => setAddress({ ...address, phone: e.target.value })}
                error={attemptedSubmit && !address.phone}
                helperText={attemptedSubmit && !address.phone ? 'Обязательное поле' : ''}
                required
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: 2
                    }
                  }
                }}
              />
              <TextField
                fullWidth
                label="Страна"
                value={address.country}
                onChange={(e) => setAddress({ ...address, country: e.target.value })}
                error={attemptedSubmit && !address.country}
                helperText={attemptedSubmit && !address.country ? 'Обязательное поле' : ''}
                required
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: 2
                    }
                  }
                }}
              />
              <TextField
                fullWidth
                label="Город"
                value={address.city}
                onChange={(e) => setAddress({ ...address, city: e.target.value })}
                error={attemptedSubmit && !address.city}
                helperText={attemptedSubmit && !address.city ? 'Обязательное поле' : ''}
                required
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: 2
                    }
                  }
                }}
              />
              <TextField
                fullWidth
                label="Улица и дом"
                value={address.street}
                onChange={(e) => setAddress({ ...address, street: e.target.value })}
                error={attemptedSubmit && !address.street}
                helperText={attemptedSubmit && !address.street ? 'Обязательное поле' : ''}
                required
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: 2
                    }
                  }
                }}
              />
              <TextField
                fullWidth
                label="Почтовый индекс"
                value={address.postalCode}
                onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
                error={attemptedSubmit && !address.postalCode}
                helperText={attemptedSubmit && !address.postalCode ? 'Обязательное поле' : ''}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: 2
                    }
                  }
                }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button 
                onClick={() => setRewardDialog(false)}
                sx={{
                  color: '#718096',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '12px',
                  px: 3
                }}
              >
                Отмена
              </Button>
              <Button
                variant="contained"
                onClick={handleRewardClaim}
                disabled={processing}
                sx={{
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                  color: 'white',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '12px',
                  px: 3,
                  boxShadow: '0 4px 15px rgba(9, 132, 227, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)'
                  },
                  '&.Mui-disabled': {
                    background: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)'
                  }
                }}
              >
                {processing ? <CircularProgress size={24} /> : 'Подтвердить заказ'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
